<!-- ----------------------------------------------- -->
<template>
  <l-map
    :ref="mapObjName"
    :zoom="zoom"
    :min-zoom="minZoom"
    :max-zoom="maxZoom"
    :maxBounds="boundsArea"
    :center="center"
    :options="mapOptions"
    :preferCanvas="true"
    @update:zoom="updateZoom"
    @update:center="updateCenter"
    @update:bounds="updateCoord"
    @click="mapClick"
  >
    <l-tile-layer
      :url="mapRiverLineUrl"
      :visible="showMapRiverLine"
      :options="riverLineOptions"
      :zIndex="4"
    ></l-tile-layer>
    <l-tile-layer
      :url="mapInundationUrl"
      :opacity="inundationOpacity"
      :visible="showMapInundation"
      :options="inundationOptions"
      :zIndex="3"
    ></l-tile-layer>
    <l-tile-layer
      :url="mapReliefUrl"
      :opacity="0.3"
      :options="mapReliefOptions"
      :visible="mapType == 1"
      :zIndex="2"
    ></l-tile-layer>
    <l-tile-layer
      :url="mapBlankUrl"
      :options="mapBlankOptions"
      :visible="mapType == 3"
      :zIndex="2"
    ></l-tile-layer>
    <l-tile-layer
      :url="mapPaleUrl"
      :options="mapPaleOptions"
      :visible="mapType == 0 || mapType == 1"
      :zIndex="1"
    ></l-tile-layer>
    <l-tile-layer
      :url="mapSeamlessphotoUrl"
      :visible="mapType == 2"
      :options="mapSeamlessOptions"
      :zIndex="1"
    ></l-tile-layer>
    <radar-layer v-if="showRadar" :mapNum="mapNum"></radar-layer>
    <radar-line-layer v-if="showRadar && showRadarLine"></radar-line-layer>
    <town-polygon-layer v-if="showTownPolygon"></town-polygon-layer>
    <river-layer
      v-if="showRiver"
      :mapNum="mapNum"
      :mapObj="mapObj"
    ></river-layer>
    <river-warn-layer v-if="showRiverWarn" :mapNum="mapNum"></river-warn-layer>
    <surface-layer v-if="showSurface"></surface-layer>
    <telemeter-layer :mapNum="mapNum"></telemeter-layer>
    <point-layer></point-layer>
    <breakwater-layer v-if="showBreakwater"></breakwater-layer>
    <div
      v-show="radarNumber == 4"
      :class="'map-date ' + 'map' + mapNum + '-date'"
    >
      {{ displayTime }}
    </div>
  </l-map>
</template>
<!-- ----------------------------------------------- -->
<script>
import { LMap, LTileLayer } from "vue2-leaflet";
import mixin from "../../lib/mixin.js";
import RiverWarnLayer from "./layer/RiverWarnLayer";
import RiverLayer from "./layer/RiverLayer";
import TelemeterLayer from "./layer/TelemeterLayer";
import TownPolygonLayer from "./layer/TownPolygonLayer";
import PointLayer from "./layer/PointLayer";
import RadarLayer from "./layer/RadarLayer";
import RadarLineLayer from "./layer/RadarLineLayer";
import SurfaceLayer from "./layer/SurfaceLayer";
import BreakwaterLayer from "./layer/BreakwaterLayer";
import Util from "../../lib/util.js";
import MapUtil from "../../lib/mapUtil.js";
import PageLog from "../../lib/pageLog.js";

/**
 * 川の防災情報 地図画面
 * MapBase.vue
 * @author LIS
 */
export default {
  /** タグ名 */
  name: "kwb-map",
  mixins: [mixin],
  /** コンポーネント */
  components: {
    LMap,
    LTileLayer,
    RiverWarnLayer,
    RiverLayer,
    TelemeterLayer,
    TownPolygonLayer,
    PointLayer,
    RadarLayer,
    RadarLineLayer,
    SurfaceLayer,
    BreakwaterLayer,
  },
  /** データ部 */
  data() {
    return {
      /** 地図オプション */
      mapOptions: {
        /** leafletのズームコントロール表示なし */
        zoomControl: false,
        /** 属性コントロール表示なし */
        attributionControl: false,
        /** ズームアニメーション閾値 */
        zoomAnimationThreshold: 2,
      },
      /** 地図タイル(淡色地図)の設定 */
      mapPaleOptions: {
        tms: false,
        maxNativeZoom: 18,
      },
      /** 地図タイル(標高地図)の設定 */
      mapReliefOptions: {
        tms: false,
        maxNativeZoom: 15,
        minNativeZoom: 5,
      },
      /** 地図タイル(航空写真)の設定 */
      mapSeamlessOptions: {
        tms: false,
        maxNativeZoom: 18,
      },
      /** 地図タイル(白地図)の設定 */
      mapBlankOptions: {
        tms: false,
        maxNativeZoom: 14,
        minNativeZoom: 5,
      },
      /** 地図タイル(浸水想定)の設定 */
      inundationOptions: {
        tms: true,
        maxNativeZoom: 15,
      },
      /** 地図タイル(河川)の設定 */
      riverLineOptions: {
        tms: true,
      },
      zoom: 0,
      center: [0, 0],
      mapObjName: "mapObj" + this.mapNum,
      /** 地図移動後の中心移動用タイマー */
      shiftCenterTimer: null,
      /** 地図移動後表示市町村取得用タイマーID */
      getTownsTimerId: null,
      mapObj: {},
    };
  },
  props: {
    mapNum: {
      type: Number,
      default: 0,
    },
  },
  /** 初期化部 */
  mounted() {
    this.updateMapInfo(this.storeMapView.center, this.storeMapView.zoom);
    this.updateCoord(this.$refs[this.mapObjName].mapObject.getBounds());
    // 地図再描画された場合を考慮し浸水想定区域図の表示データ更新
    this.updateInundationView();

    // 重なり制御のためPane設定
    this.$refs[this.mapObjName].mapObject.createPane(
      "riverNameMarkerPane"
    ).style.zIndex = 0;
    this.$refs[this.mapObjName].mapObject.createPane(
      "radarPane"
    ).style.zIndex = 400;
    this.$refs[this.mapObjName].mapObject.createPane(
      "townPane"
    ).style.zIndex = 510;
    this.$refs[this.mapObjName].mapObject.createPane(
      "riverPane"
    ).style.zIndex = 520;
    this.$refs[this.mapObjName].mapObject.createPane(
      "selectRwPane"
    ).style.zIndex = 530;
    this.$refs[this.mapObjName].mapObject.createPane(
      "evstgPane"
    ).style.zIndex = 540;
    this.$refs[this.mapObjName].mapObject.createPane(
      "fldfrPane"
    ).style.zIndex = 550;
    this.$refs[this.mapObjName].mapObject.createPane(
      "fldctlPane"
    ).style.zIndex = 560;
    this.$refs[this.mapObjName].mapObject.createPane(
      "surfacePane"
    ).style.zIndex = 570;
    this.$refs[this.mapObjName].mapObject.createPane(
      "breakwaterLinePane"
    ).style.zIndex = 580;
    this.$refs[this.mapObjName].mapObject.createPane(
      "riverNamePane"
    ).style.zIndex = 590;

    // タイルのズームレベル制限更新
    this.riverLineOptions.maxNativeZoom = this.mapConfig.zoomRiverTileEnd;
    this.riverLineOptions.minNativeZoom = this.mapConfig.zoomRiverTileStart;
    this.inundationOptions.minNativeZoom = this.mapConfig.zoomInundation;
    // 4枚表示制御のため地図オブジェクトをリストに追加
    this.$emit("push", this.$refs[this.mapObjName].mapObject);
    this.mapObj = this.$refs[this.mapObjName].mapObject;
  },
  /** 算出部 */
  computed: {
    /** 地図設定情報 */
    mapConfig: {
      get() {
        return this.$store.state.map.mapConfig;
      },
    },
    /** 現在位置 */
    currentLocation: {
      get() {
        return this.$store.state.map.currentLocation;
      },
    },
    /** 現在位置アイコンの表示設定（現在位置情報取得フラグ） */
    visibleIconPoint: {
      get() {
        return this.$store.state.map.currentLocationFlag;
      },
    },
    /** ストアのズームレベル・中心位置 */
    storeMapView: {
      get() {
        return {
          zoom: this.$store.state.map.zoom,
          center: this.$store.state.map.center,
        };
      },
    },
    /** 地図の最小ズームレベル */
    minZoom: {
      get() {
        return this.$store.state.map.minZoom;
      },
    },
    /** 地図の最大ズームレベル */
    maxZoom: {
      get() {
        return this.$store.state.map.maxZoom;
      },
    },
    /** 地図表示範囲 */
    boundsArea: {
      get() {
        return this.$store.state.map.boundsArea;
      },
    },
    /** 地図種別 */
    mapType: {
      get() {
        return this.$store.state.map.mapType;
      },
    },
    /** 地図タイル(淡色地図)のURL */
    mapPaleUrl: {
      get() {
        return this.$store.state.map.mapPaleUrl;
      },
    },
    /** 地図タイル(標高地図)のURL */
    mapReliefUrl: {
      get() {
        return this.$store.state.map.mapReliefUrl;
      },
    },
    /** 地図タイル(航空写真)のURL */
    mapSeamlessphotoUrl: {
      get() {
        return this.$store.state.map.mapSeamlessphotoUrl;
      },
    },
    /** 地図タイル(白地図)のURL */
    mapBlankUrl: {
      get() {
        return this.$store.state.map.mapBlankUrl;
      },
    },
    /** 地図タイル(浸水想定)のURL */
    mapInundationUrl: {
      get() {
        return this.$store.state.map.mapInundationUrl;
      },
    },
    /** 地図タイル(河川)のURL */
    mapRiverLineUrl: {
      get() {
        return this.$store.state.map.mapRiverLineUrl;
      },
    },
    /** モード */
    isPcMode: {
      get() {
        return this.$store.state.app.mode == "pc";
      },
    },
    /** 地図左下・右上座標の取得 */
    minmaxCoord: {
      get() {
        return [this.$store.state.map.minCoord, this.$store.state.map.maxCoord];
      },
    },
    /** 地図の枚数 [1枚, 4枚]の取得 */
    radarNumber: {
      get() {
        return this.$store.state.radar.radarNumber;
      },
    },
    /** 基準時間を計算 */
    baseTime: {
      get() {
        let time = this.$store.state.app.appTime;
        if (this.$store.state.app.isCurrent) {
          if (this.$store.state.radar.radarType == "xrain") {
            time = this.$store.state.radar.radarXrainDate;
          } else {
            time = this.$store.state.radar.radarCbandDate;
          }
        }
        return MapUtil.calcBaseTime(
          time,
          this.mapNum,
          this.$store.state.radar.radarInterval
        );
      },
    },
    /** 表示時間を取得 */
    displayTime: {
      get() {
        return Util.formatDate24(this.baseTime, "YYYY/MM/DD HH:mm");
      },
    },
    /** 浸水想定図表示制御データ */
    inundationViewData: {
      get() {
        return {
          selectParam: this.$store.state.inundation.selectedInundationParam,
          content: this.$store.state.app.content,
          zoom: this.$store.state.map.zoom,
          pointRv: this.$store.state.map.selectPointRiver,
          inundations: this.$store.state.inundation.inundations,
        };
      },
    },
    /** 浸水想定図の透過率 */
    inundationOpacity: {
      get() {
        // 不透明度に反転して、パーセント指定を少数に変換して返す
        return (100 - this.$store.state.inundation.inundationOpacity) * 0.01;
      },
    },
    /** 地図タイル(浸水想定)表示 */
    showMapInundation: {
      get() {
        return this.$store.state.map.showMapInundation;
      },
    },
    /** 地図タイル(河川)表示 */
    showMapRiverLine: {
      get() {
        // ズームレベル判定
        if (
          this.$store.state.map.zoom >= this.mapConfig.zoomRiverTileStart &&
          this.$store.state.map.zoom <= this.mapConfig.zoomRiverTileEnd
        ) {
          // 表示設定に従い、河川タイルを表示する
          return this.$store.state.map.viewRiver;
        }
        return false;
      },
    },
    /** 表示コンテンツを取得 */
    getContent: {
      get() {
        return this.$store.state.app.content;
      },
    },
    /** レーダ画像レイヤー表示制御 */
    showRadar: {
      get() {
        // ダム放流通知選択時は非表示
        if (
          this.getContent === "Riverwarn" &&
          this.$store.state.RiverWarn.selectedWarnType === 30
        ) {
          return false;
        }
        // 表示コンテンツが「浸水想定」または「表示設定」の場合非表示
        if (
          this.getContent === "Inundation" ||
          this.getContent === "VcPoint" ||
          this.getContent === "VcDefault" ||
          this.getContent === "VcCustom"
        ) {
          return false;
        }
        // 浸水想定区域図表示中は非表示
        if (this.showMapInundation) {
          return false;
        }

        // その他の場合は表示設定に従う
        return this.$store.state.map.viewRadar;
      },
    },
    /** レーダ定量範囲表示 */
    showRadarLine: {
      get() {
        return this.mapConfig.showRadarLine;
      },
    },
    /** 予警報の関連市町村レイヤー表示設定 */
    showTownPolygon: {
      get() {
        // 表示コンテンツが「河川予警報」の場合
        if (this.getContent === "Riverwarn") {
          // ズームレベルによる表示制御
          if (
            !this.$store.state.map.showMapInundation &&
            this.$store.state.map.zoom >= this.mapConfig.zoomShowTownPolygon
          ) {
            return true;
          }
        }
        return false;
      },
    },
    /** 選択中河川予警報データ */
    getSelectedRwDetail: {
      get() {
        return this.$store.state.RiverWarn.selectedRwDetail;
      },
    },
    /** 河川レイヤー表示設定 */
    showRiver: {
      get() {
        // 通常版のみ表示
        if (global.KWB_MODE != "reduction") {
          return true;
        }
        return false;
      },
    },
    /** 河川予警報レイヤー表示設定 */
    showRiverWarn: {
      get() {
        // ズームレベル判定
        if (
          this.$store.state.map.zoom >= this.mapConfig.zoomShowFldfrs ||
          this.$store.state.map.zoom >= this.mapConfig.zoomShowEvstgs ||
          this.$store.state.map.zoom >= this.mapConfig.zoomShowFldctls
        ) {
          // 表示コンテンツが表示設定の時は予警報を非表示にする
          if (this.getContent === "VcPoint") {
            return false;
          }
          // 河川予警報が選択されているか、表示コンテンツがカスタマイズ設定であれば予警報を表示する
          if (
            this.getContent === "RiverwarnList" ||
            this.getContent === "Riverwarn" ||
            this.getContent === "VcDefault" ||
            this.getContent === "VcCustom"
          ) {
            return true;
          }
        }
        // その他の場合は、表示設定に従う
        return this.$store.state.map.viewRiverWarn;
      },
    },
    /** 横断図レイヤー表示設定 */
    showSurface: {
      get() {
        // 水位観測所表示中かつ、AB点表示有の場合に表示する
        if (
          this.getContent === "TmObs" &&
          ((this.$route.query["itmkndCd"] == 4 &&
            this.$store.state.Telemeter.surfaceConf.dispAB) ||
            (this.$route.query["itmkndCd"] == 300 &&
              this.$store.state.Swin.surfaceConf.dispAB))
        ) {
          // 横断図表示エラーの場合は表示しない
          if (
            (this.$route.query["itmkndCd"] == 4 &&
              this.$store.state.Telemeter.crossSvgError) ||
            (this.$route.query["itmkndCd"] == 300 &&
              this.$store.state.Swin.swCrossSvgError)
          ) {
            return false;
          }
          if (this.$store.state.map.zoom >= this.mapConfig.zoomSurface) {
            return true;
          }
        }
        return false;
      },
    },
    /** 破堤点レイヤー表示設定 */
    showBreakwater: {
      get() {
        // 地点登録情報画面表示中の場合に表示する
        if (
          this.getContent === "RegistPoint" &&
          this.$store.state.map.zoom >= this.mapConfig.zoomBreakwater
        ) {
          return true;
        }
        return false;
      },
    },
    /** 選択中テレメータ情報 */
    getSelectedTm: {
      get() {
        return this.$store.state.Telemeter.selectedTm;
      },
    },
    /** 選択中テレメータ情報(危機管理水位) */
    getSelectedSw: {
      get() {
        return this.$store.state.Swin.selectedSw;
      },
    },
    /** 選択登録地点 */
    selectPointIndex: {
      get() {
        return this.$store.state.map.selectPointIndex;
      },
    },
    /** 地図移動アニメーション情報 */
    flyToInfo: {
      get() {
        return this.$store.state.map.flyToInfo;
      },
    },
    /** 地図移動情報 */
    moveInfo: {
      get() {
        return this.$store.state.map.moveInfo;
      },
    },
    /** 表示市町村取得時刻 */
    townsAcqTime: {
      get() {
        return this.$store.state.map.townsAcqTime;
      },
    },
    /** 都道府県別市町村座標リスト */
    latLngListByPref: {
      get() {
        return this.$store.state.map.latLngListByPref;
      },
    },
    /** 市町村座標リスト */
    townLatLngList: {
      get() {
        return this.$store.state.map.townLatLngList;
      },
    },
    /** 地図再描画のためのコンポーネントのKey */
    mapComponentKey: {
      get() {
        return this.$store.state.map.mapComponentKey;
      },
    },
    /** 表示市町村一覧 */
    towns: {
      get() {
        return this.$store.state.map.towns;
      },
    },
  },
  methods: {
    /** ズームレベル変更時処理 */
    updateZoom(level) {
      // 地図番号が0(1枚表示か4枚表示の1枚目)のときのみ処理を行う
      if (this.mapNum == 0) {
        this.$store.commit("map/setViewZoom", level);
      }
    },
    /** 中心座標更新時処理 */
    updateCenter(latLng) {
      // 地図番号が0(1枚表示か4枚表示の1枚目)のときのみ処理を行う
      if (this.mapNum == 0) {
        this.$store.commit("map/setCenter", [latLng.lat, latLng.lng]);
      }
    },
    /** 右上左下座標更新時処理 */
    updateCoord(latLngBounds) {
      // 地図番号が0(1枚表示か4枚表示の1枚目)のときのみ処理を行う
      if (this.mapNum == 0) {
        this.$store.commit("map/setCoord", latLngBounds);
      }
    },
    /** マップクリック時処理 */
    mapClick(e) {
      // 登録地編集モードの場合、登録地点選択情報を更新する
      if (this.$store.state.map.editLocationMode === 1) {
        this.$store.commit("map/setEditLocation", {
          zoom: this.$store.state.map.zoom,
          latlng: [e.latlng.lat, e.latlng.lng],
        });
      }
    },
    // 地図のズームレベル・中心位置を同時に更新
    updateMapInfo(center, zoom) {
      this.$refs[this.mapObjName].mapObject.setView(center, zoom);
    },
    /** 地図中央座標からエリア、都道府県、市町村を抽出 */
    setCenterAreaPrefTown() {
      const zoom = this.storeMapView.zoom;
      const center = this.storeMapView.center;

      let areaCd = null;
      let areaNm = "";
      let prefNm = "";
      let townNm = "";
      let distanceDiff = 10 * Math.pow(10, 9);

      // ズームレベルに関わらず都道府県とエリアを検索
      this.mapConfig.prefLatLngList.forEach((pref) => {
        // 表示都道府県一覧に含まれる県のみ対象とする
        if (
          this.$store.state.map.prefs.find((tgt) => {
            return tgt.id == pref.prefCd;
          })
        ) {
          // "北海道"は"北海道道央"などと同列に扱えないためスキップ
          if (pref.prefCd == 101) {
            return;
          }
          // 比較対象と中心座標の緯度経度の差分を算出
          // 小数点計算の誤差を無くすため10*9倍にして計算する
          let targetDiff =
            Math.abs(
              center[0] * Math.pow(10, 9) - pref.center[0] * Math.pow(10, 9)
            ) +
            Math.abs(
              center[1] * Math.pow(10, 9) - pref.center[1] * Math.pow(10, 9)
            );
          // 差分が小さい(=中心に近い)場合は更新
          if (distanceDiff > targetDiff) {
            distanceDiff = targetDiff;
            prefNm = pref.prefNm;
            areaCd = pref.areaCd;
            areaNm = pref.areaNm;
          }
        }
      });

      // 市町村
      let neraTown = {};
      if (zoom >= this.mapConfig.zoomCity) {
        neraTown = MapUtil.getNearTown(
          center,
          this.latLngListByPref,
          this.$store.state.map.prefs
        );
        prefNm = neraTown.prefNm;
        townNm = neraTown.townNm;
      }
      // ストアに格納
      this.$store.commit("map/setTownName", {
        prefName: prefNm,
        townName: townNm,
        areaName: areaNm,
        areaCd: areaCd,
        zoom: zoom,
      });
      //
    },
    /** 河川予警報選択時の地図移動 */
    moveRw() {
      // 関連市町村の緯度経度を取得
      let nLat = 0;
      let eLon = 0;
      let sLat = 999;
      let wLon = 999;
      this.getSelectedRwDetail.mt.prefTwn.forEach((pref) => {
        pref.twn.forEach((twn) => {
          // 市町村情報を取得
          const target = this.$store.state.map.townLatLngList.find(
            (townInfo) => {
              return townInfo.twnCd == twn.twnCd;
            }
          );
          if (target) {
            // 最大と最小の緯度経度を設定
            if (target.maxLat > nLat) nLat = target.maxLat;
            if (target.maxLon > eLon) eLon = target.maxLon;
            if (target.minLat < sLat) sLat = target.minLat;
            if (target.minLon < wLon) wLon = target.minLon;
          }
        });
      });
      // 中心座標を設定
      let latLng = [];
      if (this.$store.state.RiverWarn.selectedWarnType == 30) {
        // ダム放流通知
        latLng = [
          this.getSelectedRwDetail.mt.lat,
          this.getSelectedRwDetail.mt.lon,
        ];
      } else {
        // 市町村座標の最大最小から中心を算出
        // 小数点計算の誤差を無くすため10*9倍にして計算する
        latLng = [
          (nLat * Math.pow(10, 9) + sLat * Math.pow(10, 9)) /
            2 /
            Math.pow(10, 9),
          (eLon * Math.pow(10, 9) + wLon * Math.pow(10, 9)) /
            2 /
            Math.pow(10, 9),
        ];
      }

      // 市町村座標を取得できていない場合は移動しない
      if (nLat != 0 && eLon != 0 && sLat != 999 && wLon != 999) {
        this.$store.dispatch("map/moveMapRw", {
          type: this.$store.state.RiverWarn.selectedWarnType,
          latLng: latLng,
          nLat: nLat,
          eLon: eLon,
          sLat: sLat,
          wLon: wLon,
        });
      }

      // 予警報が変わらない場合は移動しない制御のために、選択予警報を保持しておく
      this.$store.commit(
        "map/setSelectRw",
        this.$store.state.RiverWarn.selectedWarnReq
      );
    },
    /** 地図移動後の中心移動 */
    shiftCenter() {
      let lat = this.storeMapView.center[0];
      let lon = this.storeMapView.center[1];
      if (window.innerWidth < window.innerHeight) {
        // 画面が縦長の場合は上に1/4ずらす
        lat =
          lat -
          (this.$store.state.map.maxCoord[0] -
            this.$store.state.map.minCoord[0]) /
            4;
      } else {
        // 画面が横長の場合は左に1/4ずらす
        lon =
          lon +
          (this.$store.state.map.maxCoord[1] -
            this.$store.state.map.minCoord[1]) /
            4;
      }
      // 直後だとleafletが反応しないため時間差で移動
      this.shiftCenterTimer = setTimeout(
        function (obj) {
          this.$store.commit("map/setFlyToInfo", obj);
        }.bind(this),
        500,
        {
          zoom: this.storeMapView.zoom,
          center: [lat, lon],
        }
      );
    },
    /** 浸水想定一覧を取得 */
    getInundations() {
      // 登録地点情報表示中のみ浸水想定区域一覧を取得する
      if (
        this.getContent === "RegistPoint" &&
        this.$store.state.map.zoom >= this.mapConfig.zoomInundation
      ) {
        this.$store.dispatch("inundation/getInundations", {
          townsAcqTime: this.townsAcqTime,
          towns: this.$store.state.map.towns,
        });
      }
    },
    /** 浸水想定区域図の表示データ更新 */
    updateInundationView() {
      // 浸水想定画面
      if (
        this.inundationViewData.content === "Inundation" &&
        this.inundationViewData.selectParam &&
        this.inundationViewData.selectParam.inundationId &&
        this.inundationViewData.zoom >= this.mapConfig.zoomInundation
      ) {
        this.$store.commit("map/setMapInundation", {
          flg: true,
          inundationId: this.inundationViewData.selectParam.inundationId,
        });
        return;
      }

      // 登録地点情報画面
      if (
        this.inundationViewData.content === "RegistPoint" &&
        this.inundationViewData.pointRv.length > 0 &&
        this.inundationViewData.inundations.length > 0 &&
        this.inundationViewData.zoom >= this.mapConfig.zoomInundation
      ) {
        // 選択河川と前方一致する浸水想定河川区域があるか判定
        for (
          let iCnt = 0;
          iCnt < this.inundationViewData.inundations.length;
          iCnt++
        ) {
          const inundationId =
            this.inundationViewData.inundations[iCnt].inundationId;
          for (
            let pCnt = 0;
            pCnt < this.inundationViewData.pointRv.length;
            pCnt++
          ) {
            const rvrCd = String(this.inundationViewData.pointRv[pCnt].rvrCd);
            if (inundationId.startsWith(rvrCd)) {
              this.$store.commit("map/setMapInundation", {
                flg: true,
                inundationId: inundationId,
              });
              return;
            }
          }
        }
      }

      // 発表情報詳細画面
      if (
        this.inundationViewData.content === "Riverwarn" &&
        this.inundationViewData.selectParam &&
        this.inundationViewData.zoom >= this.mapConfig.zoomInundation
      ) {
        if (this.inundationViewData.selectParam.inundationId) {
          this.$store.commit("map/setMapInundation", {
            flg: true,
            inundationId: this.inundationViewData.selectParam.inundationId,
          });
          return;
        } else {
          this.$store.commit("map/setMapInundation", {
            flg: false,
            inundationId: null,
          });
          return;
        }
      }

      // 浸水想定区域IDが特定できない場合はクリア
      this.$store.commit("map/setMapInundation", {
        flg: false,
        inundationId: null,
      });
    },
  },
  watch: {
    /** 地図移動を監視 */
    minmaxCoord() {
      // 地図移動した場合、中心移動用タイマーは破棄
      clearTimeout(this.shiftCenterTimer);
      // 地図番号が0(1枚表示か4枚表示の1枚目)のときのみ処理を行う
      if (this.mapNum == 0) {
        // URLパラメータ更新
        this.routerMapMove();

        // レーダの履歴動画再生状態をリセット
        if (this.$store.getters["radar/isStop"]) {
          this.$store.commit("radar/resetHistoryPlay");
        }
        // 観測所一覧ポップアップを閉じる
        this.$store.commit("map/setShowTmList", false);

        // 地図ヘッダー表示市町村を更新
        this.setCenterAreaPrefTown();

        // 地図移動が連続して行われた際に途中の状態での取得を行わないように
        // 表示市町村取得は地図移動後一定時間停止している場合のみ行う
        clearTimeout(this.getTownsTimerId);
        this.getTownsTimerId = setTimeout(
          function () {
            this.$store.dispatch("map/getTowns");
          }.bind(this),
          this.$store.state.map.mapConfig.updateWaitTime
        );
      }

      // 予警報選択時移動中の場合、表示範囲に収まっているか確認
      if (this.$store.state.map.selectRwMoveFlg) {
        // 小数点計算の誤差を無くすため10*9倍にして計算する
        if (
          this.$store.state.map.maxCoord[0] * Math.pow(10, 9) <
            this.$store.state.map.selectRwRange.nLat * Math.pow(10, 9) ||
          this.$store.state.map.maxCoord[1] * Math.pow(10, 9) <
            this.$store.state.map.selectRwRange.eLon * Math.pow(10, 9) ||
          this.$store.state.map.minCoord[0] * Math.pow(10, 9) >
            this.$store.state.map.selectRwRange.sLat * Math.pow(10, 9) ||
          this.$store.state.map.minCoord[1] * Math.pow(10, 9) >
            this.$store.state.map.selectRwRange.wLon * Math.pow(10, 9)
        ) {
          // 収まっていない場合は、ズームレベルを一つ下げる
          if (this.$store.state.map.zoom > this.mapConfig.minZoom) {
            this.$store.commit("map/setMoveInfo", {
              zoom: this.$store.state.map.zoom - 1,
              center: this.$store.state.map.center,
            });
            return;
          }
        }
        // 移動完了
        this.$store.commit("map/setSelectRwMoveFlg", {
          flg: false,
          nLat: null,
          eLon: null,
          sLat: null,
          wLon: null,
        });
      }

      if (this.$store.state.map.shiftCenterFlg) {
        this.$store.commit("map/setShiftCenterFlg", false);
        // モバイルモードの場合のみ、移動後に中心座標をずらす
        if (!this.isPcMode) {
          this.shiftCenter();
        }
      }
    },
    /** ストアのズームレベル・中心位置を監視 */
    storeMapView() {
      this.routerMapMove();
    },
    /** 地図移動情報を監視 */
    moveInfo() {
      this.updateMapInfo(this.moveInfo.center, this.moveInfo.zoom);
    },
    /** 地図移動アニメーション情報を監視 */
    flyToInfo() {
      this.$refs[this.mapObjName].mapObject.flyTo(
        this.flyToInfo.center,
        this.flyToInfo.zoom,
        { duration: 0.5 }
      );
    },
    /** 地図再描画のためのコンポーネントのKeyを監視 */
    mapComponentKey() {
      // コンポーネントKeyか更新された際に地図中心をリセットする
      this.$refs[this.mapObjName].mapObject._onResize();
    },
    /** 浸水想定図表示制御データを監視 */
    inundationViewData() {
      // 浸水想定区域図の表示データ更新
      this.updateInundationView();
    },
    /** 選択中河川予警報データを監視 */
    getSelectedRwDetail() {
      if (
        Object.keys(this.getSelectedRwDetail).length > 0 &&
        this.getContent === "Riverwarn"
      ) {
        const selectedWarnReq = this.$store.state.RiverWarn.selectedWarnReq;
        // 予警報が変わっていない場合は移動しない
        if (
          this.$store.state.map.selectRw &&
          this.$store.state.map.selectRw.cd == selectedWarnReq.cd &&
          this.$store.state.map.selectRw.type == selectedWarnReq.type
        ) {
          return;
        }
        // 地図移動
        this.moveRw();
      }
    },
    /** 選択中テレメータ情報を監視 */
    getSelectedTm() {
      // 観測所タブ選択時に地図移動して既定のズームレベルにする
      if (
        Object.keys(this.getSelectedTm).length > 0 &&
        this.getContent === "TmObs"
      ) {
        const tmParam = this.$store.state.Telemeter.selectedTmParam;
        // 観測所が変わっていない場合は移動しない
        if (tmParam.itmkndCd == 100 || tmParam.itmkndCd == 200) {
          if (
            this.$store.state.map.selectTm &&
            this.$store.state.map.selectTm.itmkndCd == tmParam.itmkndCd &&
            this.$store.state.map.selectTm.scamId == tmParam.scamId
          ) {
            return;
          }
        } else {
          if (
            this.$store.state.map.selectTm &&
            this.$store.state.map.selectTm.itmkndCd == tmParam.itmkndCd &&
            this.$store.state.map.selectTm.ofcCd == tmParam.ofcCd &&
            this.$store.state.map.selectTm.obsCd == tmParam.obsCd
          ) {
            return;
          }
        }

        let latLng = [];
        let zoom = null;
        switch (tmParam.itmkndCd) {
          case 4:
            // 水位観測所
            latLng = [
              this.getSelectedTm.tmStgDetailInfo.lat,
              this.getSelectedTm.tmStgDetailInfo.lon,
            ];
            zoom = this.mapConfig.zoomTmStg;
            break;
          case 1:
            // 雨量観測所
            latLng = [
              this.getSelectedTm.tmRainDetailInfo.lat,
              this.getSelectedTm.tmRainDetailInfo.lon,
            ];
            zoom = this.mapConfig.zoomTmRn;
            break;
          case 2:
            // 流域平均雨量観測所
            latLng = [
              this.getSelectedTm.tmBsnDetailInfo.lat,
              this.getSelectedTm.tmBsnDetailInfo.lon,
            ];
            zoom = this.mapConfig.zoomTmRn;
            break;
          case 20:
            // レーダ流域平均雨量（大流域）観測所
            latLng = [
              this.getSelectedTm.tmRdBsnDetailInfo.lat,
              this.getSelectedTm.tmRdBsnDetailInfo.lon,
            ];
            zoom = this.mapConfig.zoomTmRn;
            break;
          case 21:
            // レーダ流域平均雨量（小流域）観測所
            latLng = [
              this.getSelectedTm.tmRdBsnDetailInfo.lat,
              this.getSelectedTm.tmRdBsnDetailInfo.lon,
            ];
            zoom = this.mapConfig.zoomTmRn;
            break;
          case 6:
            // 水質観測所
            latLng = [
              this.getSelectedTm.tmQwtrDetailInfo.lat,
              this.getSelectedTm.tmQwtrDetailInfo.lon,
            ];
            zoom = this.mapConfig.zoomTmQwtr;
            break;
          case 7:
            // ダム諸量観測所
            latLng = [
              this.getSelectedTm.tmDamDetailInfo.lat,
              this.getSelectedTm.tmDamDetailInfo.lon,
            ];
            zoom = this.mapConfig.zoomTmDam;
            break;
          case 12:
            // 海岸観測所
            latLng = [
              this.getSelectedTm.tmCoastDetailInfo.lat,
              this.getSelectedTm.tmCoastDetailInfo.lon,
            ];
            zoom = this.mapConfig.zoomTmCoast;
            break;
          case 3:
            // 積雪深観測所
            latLng = [
              this.getSelectedTm.tmSnwDetailInfo.lat,
              this.getSelectedTm.tmSnwDetailInfo.lon,
            ];
            zoom = this.mapConfig.zoomTmSnw;
            break;
          case 100:
            // 河川カメラの処理
            latLng = [
              this.getSelectedTm.cctvDetailInfo.lat,
              this.getSelectedTm.cctvDetailInfo.lon,
            ];
            zoom = this.mapConfig.zoomTmCctv;
            break;
          case 200:
            // 簡易カメラの処理
            latLng = [
              this.getSelectedTm.scamDetailInfo.lat,
              this.getSelectedTm.scamDetailInfo.lon,
            ];
            zoom = this.mapConfig.zoomTmScam;
            break;
          default:
            return;
        }

        // 地図アイコンからの遷移フラグ
        if (this.$store.state.Telemeter.mapIconFlg) {
          // zoom = this.$store.state.map.zoom;
          this.$store.commit("Telemeter/setMapIconFlg", false);
          // 観測所が変更されたら過去画像動画状態をリセット
          this.$store.commit("Telemeter/resetHistoryPlay");
          this.$store.commit("Telemeter/resetHistoryPrintCurrentPicture");
        } else {
          this.$store.dispatch("map/moveMapTm", {
            latLng: latLng,
            tmZoom: zoom,
          });
        }

        // 観測所が変わらない場合は移動しない制御のために、選択観測所を保持しておく
        this.$store.commit("map/setSelectTm", tmParam);
      }
    },
    /** 選択中テレメータ情報(危機管理水位)を監視 */
    getSelectedSw() {
      // 観測所タブ選択時に地図移動して既定のズームレベルにする
      if (
        Object.keys(this.getSelectedSw).length > 0 &&
        this.getContent === "TmObs"
      ) {
        const tmParam = this.$store.state.Swin.selectedSwParam;

        // 観測所が変わっていない場合は移動しない
        if (
          this.$store.state.map.selectTm &&
          this.$store.state.map.selectTm.itmkndCd == tmParam.itmkndCd &&
          this.$store.state.map.selectTm.swobsCd == tmParam.swobsCd
        ) {
          return;
        }

        let latLng = [
          this.getSelectedSw.mtSwobs.obsInfo.lat,
          this.getSelectedSw.mtSwobs.obsInfo.lon,
        ];
        let zoom = this.mapConfig.zoomTmSwstg;
        // 地図アイコンからの遷移フラグ
        if (this.$store.state.Telemeter.mapIconFlg) {
          // zoom = this.$store.state.map.zoom;
          this.$store.commit("Telemeter/setMapIconFlg", false);
          // 観測所が変更されたら過去画像動画状態をリセット
          this.$store.commit("Telemeter/resetHistoryPlay");
          this.$store.commit("Telemeter/resetHistoryPrintCurrentPicture");
        } else {
          this.$store.dispatch("map/moveMapTm", {
            latLng: latLng,
            tmZoom: zoom,
          });
        }

        // 観測所が変わらない場合は移動しない制御のために、選択観測所を保持しておく
        this.$store.commit("map/setSelectTm", tmParam);
      }
    },
    /** 選択登録地点を監視 */
    selectPointIndex() {
      if (this.getContent === "RegistPoint") {
        this.$store.dispatch("map/moveMapPoint", {
          latLng: this.$store.state.map.point.get(this.selectPointIndex + 1)
            .center,
          flyto: true,
        });
      }
    },
    /** 表示市町村取得時刻を監視 */
    townsAcqTime() {
      // 地図ヘッダー表示市町村を更新
      this.setCenterAreaPrefTown();
      // 浸水想定区域一覧を取得(登録地点情報画面表示時のみ)
      this.getInundations();
    },
    /** 都道府県別市町村座標リストを監視 */
    latLngListByPref() {
      // 地図ヘッダー表示市町村を更新
      this.setCenterAreaPrefTown();
    },
    /** 市町村座標リストを監視 */
    townLatLngList() {
      // 表示市町村リスト取得
      this.$store.dispatch("map/getTowns");
      // 予警報選択時は地図移動する
      if (
        Object.keys(this.getSelectedRwDetail).length > 0 &&
        this.getContent === "Riverwarn"
      ) {
        this.moveRw();
      }
    },
    /** 表示コンテンツを監視 */
    getContent() {
      // 浸水想定区域一覧を取得(登録地点情報画面表示時のみ)
      this.getInundations();
    },
    /** 地図タイル(浸水想定)のURLを監視 */
    mapInundationUrl() {
      // アクセスログ出力
      // 浸水想定画面表示中のみ出力
      if (this.getContent == "Inundation") {
        let logInumCd = "-";
        if (
          this.showMapInundation &&
          this.$store.state.inundation.selectedInundationParam.inundationId
        ) {
          logInumCd =
            this.$store.state.inundation.selectedInundationParam.inundationId;
        }
        PageLog.log({
          page_log: "inundation",
          lat: this.$store.state.map.center[0],
          lon: this.$store.state.map.center[1],
          zm: this.$store.state.map.zoom,
          device_sb: this.$store.state.app.mode,
          inun_cd: logInumCd,
          fc_flg: this.$store.getters["app/isFldCtl"],
        });
      }
    },
    /** 表示市町村一覧 */
    towns() {
      this.$store.dispatch("map/getCenterTown", {
        center: this.$store.state.map.center,
        twnList: this.towns,
      });
    },
  },
};
</script>
<!-- ----------------------------------------------- -->
<style lang="scss" scoped>
@import "./../../scss/kwb.scss";
.map-date {
  position: absolute;
  z-index: 3020;
  padding: 4px;
  background: $color-map-date-back;
  color: $color-map-date-text;
  font-size: 0.9rem;
  font-weight: bold;
}
.map3-date {
  bottom: 4px;
  right: 4px;
}
.map2-date {
  bottom: 4px;
  left: 4px;
}
.map1-date {
  top: 4px;
  right: 4px;
}
.map0-date {
  top: 4px;
  left: 4px;
}
</style>
